import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/apiService';
import LandingArtefactBlobTile from '../components/LandingArtefactBlobTile';
import { mergeBlobVariantConfig } from '../components/blob/blobVariantConfig';
import {
  isProbioticArchitectureName,
  resolveArtefactBlobConfig,
} from '../components/blob/resolveArtefactBlobConfig';
import {
  markFreshArtefactFromIntro,
  resetClientSessionWhenReturningHome,
} from '../utils/chatSessionStorage';
import { artefactPath } from '../utils/artefactSessionPath';
import {
  buildArtefactColorMap,
  fetchKeywordAnalyticsForMarquee,
  fetchLandingBlobColorAnalytics,
  getTemporalKeywordList,
  getTopKeywordList,
  LANDING_MAX_ARTEFACT_CELLS,
  LANDING_REFRESH_MS,
  orderedLandingArtefactNames,
} from '../utils/landingPageAnalytics';
import { DEFAULT_LANDING_BLOB_GRADIENT } from '../utils/landingBlobGifs';
import { unlockAudioPlayback } from '../utils/audioUnlock';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import LandingCarouselSwipeCard from '../components/LandingCarouselSwipeCard';
import LandingKeywordMarquee from '../components/LandingKeywordMarquee';
import OverviewFooterLinks from '../components/OverviewFooterLinks';
import './IntroPage.css';
import './OverviewPage.css';

/** Below: single-artefact carousel; at/above: grid */
const LANDING_CAROUSEL_MAX_PX = 767;

/** Grid uses split title+blob cards at/above; iPad keeps hover-only intro below this width. */
const LANDING_WIDE_DESKTOP_MIN_PX = 1280;

/** Landing blobs: lighter mesh than chat/aura (detail 100 ≈ 612k verts; 40 ≈ 101k). */
const LANDING_BLOB_ICOSAHEDRON_DETAIL = 40;
const PROBIOTIC_LANDING_BLOB_ICOSAHEDRON_DETAIL = 85;

/** Chooser entries disabled until full persona content exists in personas.json. */
const INTRO_DISABLED_ARTEFACTS = new Set([

]);

/** Title case for card headings (curated exhibition style vs all-caps). */
function formatArtefactDisplayTitle(raw) {
  if (typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function personaCardTitle(personasMap, personaKey) {
  const p = personasMap?.[personaKey];
  const title = typeof p?.display_title === 'string' ? p.display_title.trim() : '';
  const displayName = typeof p?.display_name === 'string' ? p.display_name.trim() : '';
  const base = title || displayName || personaKey;
  return formatArtefactDisplayTitle(base);
}

/** Landing copy: prefer introduction_text, then intro; omit [STUB] placeholders (e.g. Greek JSON before merge). */
function landingCardIntroText(persona) {
  if (!persona || typeof persona !== 'object') return '';
  const candidates = [persona.introduction_text, persona.intro];
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const s = raw.trim();
    if (!s || s.startsWith('[STUB]')) continue;
    return s;
  }
  return '';
}

function IntroPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [carouselMode, setCarouselMode] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= LANDING_CAROUSEL_MAX_PX
  );
  const [wideDesktopLayout, setWideDesktopLayout] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= LANDING_WIDE_DESKTOP_MIN_PX
  );
  const [personas, setPersonas] = useState({});
  const [personaOrder, setPersonaOrder] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [artefactColors, setArtefactColors] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);
  /** 1 = next (e.g. swipe left), -1 = previous; drives enter animation direction. */
  const carouselSlideIntentRef = useRef(0);
  /** Latest personas for keyword poll without recreating the interval callback. */
  const personasRef = useRef(personas);
  const personaOrderRef = useRef(personaOrder);
  const languageRef = useRef(language);
  /** Bumps when a new landing keyword fetch starts; drop stale async results (e.g. slow EN after switch to EL). */
  const landingKeywordFetchEpochRef = useRef(0);
  personasRef.current = personas;
  personaOrderRef.current = personaOrder;
  languageRef.current = language;

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setCarouselMode(w <= LANDING_CAROUSEL_MAX_PX);
      setWideDesktopLayout(w >= LANDING_WIDE_DESKTOP_MIN_PX);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') return;
    let cancelled = false;
    void (async () => {
      await resetClientSessionWhenReturningHome(apiService);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  /** Full load: personas + keyword analytics (mount and language change only). */
  const fetchLandingDataInitial = useCallback(async () => {
    landingKeywordFetchEpochRef.current += 1;
    const keywordFetchEpoch = landingKeywordFetchEpochRef.current;
    try {
      setError('');
      setIsDataLoading(true);

      const [personasResult, keywordAnalyticsData, colorAnalyticsData] = await Promise.all([
        apiService.getPersonas(language),
        fetchKeywordAnalyticsForMarquee(apiService, language),
        fetchLandingBlobColorAnalytics(apiService, language),
      ]);

      if (keywordFetchEpoch !== landingKeywordFetchEpochRef.current) {
        return;
      }

      if (personasResult?.success && personasResult.personas) {
        setPersonas(personasResult.personas);
        setPersonaOrder(Array.isArray(personasResult.persona_order) ? personasResult.persona_order : []);
      }

      const topKeywords = getTopKeywordList(keywordAnalyticsData);
      const temporalKeywords = getTemporalKeywordList(keywordAnalyticsData);

      const order = Array.isArray(personasResult?.persona_order) ? personasResult.persona_order : [];
      const orderedNames = orderedLandingArtefactNames(
        personasResult?.success ? personasResult.personas : {},
        order
      );
      const nextColors = buildArtefactColorMap(
        orderedNames,
        colorAnalyticsData,
        personasResult?.success ? personasResult.personas : null
      );

      setKeywords(temporalKeywords.length > 0 ? temporalKeywords : topKeywords);
      setArtefactColors(nextColors);

      if (!personasResult?.success || !personasResult?.personas) {
        setError('Failed to load landing data from backend');
      }
    } catch (fetchError) {
      if (keywordFetchEpoch !== landingKeywordFetchEpochRef.current) {
        return;
      }
      console.error('Landing data fetch failed:', fetchError);
      setError('Failed to load landing data from backend');
      setKeywords([]);
    } finally {
      if (keywordFetchEpoch === landingKeywordFetchEpochRef.current) {
        setIsDataLoading(false);
      }
    }
  }, [language]);

  /**
   * Poll keyword analytics only (stable identity — reads personas from refs so the 30s timer
   * does not retrigger the effect that runs the full persona fetch).
   */
  const refreshLandingKeywordsOnly = useCallback(async () => {
    const p = personasRef.current;
    const order = personaOrderRef.current;
    if (Object.keys(p).length === 0) return;
    const epochSnapshot = landingKeywordFetchEpochRef.current;
    try {
      const [keywordAnalyticsData, colorAnalyticsData] = await Promise.all([
        fetchKeywordAnalyticsForMarquee(apiService, languageRef.current),
        fetchLandingBlobColorAnalytics(apiService, languageRef.current),
      ]);

      const topKeywords = getTopKeywordList(keywordAnalyticsData);
      const temporalKeywords = getTemporalKeywordList(keywordAnalyticsData);
      const orderedNames = orderedLandingArtefactNames(p, order);
      const nextColors = buildArtefactColorMap(orderedNames, colorAnalyticsData, p);

      if (epochSnapshot !== landingKeywordFetchEpochRef.current) {
        return;
      }

      setKeywords(temporalKeywords.length > 0 ? temporalKeywords : topKeywords);
      setArtefactColors(nextColors);
    } catch (e) {
      console.warn('Landing keyword poll failed:', e);
    }
  }, []);

  useEffect(() => {
    void fetchLandingDataInitial();
  }, [fetchLandingDataInitial]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refreshLandingKeywordsOnly();
    }, LANDING_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [refreshLandingKeywordsOnly]);

  const artefactNames = useMemo(() => {
    const keys = Object.keys(personas);
    const ordered = personaOrder.filter((n) => keys.includes(n));
    const rest = keys.filter((n) => !ordered.includes(n));
    const active = [...ordered, ...rest];
    const stubs = [...INTRO_DISABLED_ARTEFACTS].filter((n) => !active.includes(n));
    stubs.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const merged = [...active, ...stubs];
    return merged.slice(0, LANDING_MAX_ARTEFACT_CELLS);
  }, [personas, personaOrder]);

  useEffect(() => {
    carouselSlideIntentRef.current = 0;
    setActiveSlide((i) => {
      if (artefactNames.length === 0) return 0;
      return Math.min(i, artefactNames.length - 1);
    });
  }, [artefactNames.length]);

  useEffect(() => {
    if (!carouselMode) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        carouselSlideIntentRef.current = -1;
        setActiveSlide((i) => Math.max(0, i - 1));
      } else if (e.key === 'ArrowRight') {
        carouselSlideIntentRef.current = 1;
        setActiveSlide((i) => Math.min(artefactNames.length - 1, i + 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [carouselMode, artefactNames.length]);

  const onCarouselSwipeNext = useCallback(() => {
    setActiveSlide((i) => {
      const next = Math.min(artefactNames.length - 1, i + 1);
      if (next === i) {
        carouselSlideIntentRef.current = 0;
        return i;
      }
      carouselSlideIntentRef.current = 1;
      return next;
    });
  }, [artefactNames.length]);

  const onCarouselSwipePrev = useCallback(() => {
    setActiveSlide((i) => {
      const next = Math.max(0, i - 1);
      if (next === i) {
        carouselSlideIntentRef.current = 0;
        return i;
      }
      carouselSlideIntentRef.current = -1;
      return next;
    });
  }, []);

  const landingBlobPropsFor = useCallback(
    (artefactName, { landingGridIndex, landingGridCount, useDefaultGradient = false } = {}) => {
      const softenOverheadKey =
        typeof landingGridIndex === 'number' &&
        typeof landingGridCount === 'number' &&
        landingGridCount >= 2 &&
        landingGridIndex >= landingGridCount - 2;
      const gradient = useDefaultGradient
        ? DEFAULT_LANDING_BLOB_GRADIENT
        : artefactColors[artefactName] || DEFAULT_LANDING_BLOB_GRADIENT;
      const config = mergeBlobVariantConfig(resolveArtefactBlobConfig(artefactName), 'landing');
      const cardTitle = personaCardTitle(personas, artefactName);
      const probioticLandingBlob = isProbioticArchitectureName(artefactName || cardTitle);
      return {
        artefactName,
        cardTitle,
        gradient,
        config,
        softenOverheadKey,
        icosahedronDetail: probioticLandingBlob
          ? PROBIOTIC_LANDING_BLOB_ICOSAHEDRON_DETAIL
          : LANDING_BLOB_ICOSAHEDRON_DETAIL,
      };
    },
    [artefactColors, personas]
  );

  const temporalKeywordStrings = useMemo(() => {
    return keywords
      .map((item) => item.keyword)
      .filter((keyword) => typeof keyword === 'string' && keyword.length > 0);
  }, [keywords]);

  /** Enough items for a smooth marquee when the API returns few keywords */
  const marqueeKeywordRows = useMemo(() => {
    const raw = temporalKeywordStrings;
    if (raw.length === 0) return [];
    const minItems = 12;
    const out = [];
    while (out.length < minItems) {
      out.push(...raw);
    }
    return out;
  }, [temporalKeywordStrings]);

  const handleArtefactActivate = useCallback(
    async (personaName) => {
      if (INTRO_DISABLED_ARTEFACTS.has(personaName)) return;
      unlockAudioPlayback();
      const persona = personas[personaName];
      const originalPersonaName = persona?.original_name || personaName;

      await resetClientSessionWhenReturningHome(apiService);
      markFreshArtefactFromIntro();

      navigate(artefactPath(personaName, 'disclaimer', personas), {
        state: {
          selectedPersona: personaName,
          displayPersonaName: personaName,
        },
      });
    },
    [navigate, personas]
  );

  const layoutClass = carouselMode ? 'mobile-layout' : 'desktop-layout';

  const overviewLandingHeader = (
    <header className="App-header overview-page-header">
      <div className="header-left overview-page-header__brand">
        <p className="overview-kicker">{t('app.exhibitionCompanion')}</p>
        <h1 className="overview-main-title">
          <Link to="/" className="overview-main-title__link" aria-label={t('app.navHomeAria')}>
            {t('app.title')}
          </Link>
        </h1>
      </div>
      <div className="header-right">
        <LanguageSelector />
      </div>
    </header>
  );

  const renderArtefactCard = (
    artefactName,
    { compact, showBlob = true, landingGridIndex, landingGridCount } = {}
  ) => {
    const persona = personas[artefactName];
    const introText = landingCardIntroText(persona);
    const disabled = INTRO_DISABLED_ARTEFACTS.has(artefactName);
    const cardTitle = personaCardTitle(personas, artefactName);
    const blobProps = landingBlobPropsFor(artefactName, { landingGridIndex, landingGridCount });

    const blobCanvas = (
      <LandingArtefactBlobTile
        artefactName={artefactName}
        personas={personas}
        blobProps={blobProps}
        showWebGL={showBlob}
      />
    );

    const cardFooter = (
      <div className="persona-card-footer overview-card-footer">
        {disabled ? (
          <span className="click-hint">
            <em>{t('introPage.comingSoon')}</em>
          </span>
        ) : (
          <button
            type="button"
            className="overview-cta-pill"
            onClick={(e) => {
              e.stopPropagation();
              handleArtefactActivate(artefactName);
            }}
          >
            {t('app.tapToTalk')}
          </button>
        )}
      </div>
    );

    const useSplitGrid = compact && wideDesktopLayout;

    const inner = useSplitGrid ? (
      <>
        <div className="overview-exhibition-card__text">
          <div className="persona-card-header">
            <h2 className="persona-name">{cardTitle}</h2>
          </div>
          {introText ? <p className="persona-intro overview-card-intro">{introText}</p> : null}
        </div>
        <div className="overview-exhibition-card__blob">{blobCanvas}</div>
        {cardFooter}
      </>
    ) : (
      <>
        <div className="persona-card-header">
          <h2 className="persona-name">{cardTitle}</h2>
        </div>
        <div
          className={`persona-card-body overview-blob-tile-body${compact ? ' overview-blob-tile-body--compact' : ''}`}
        >
          {blobCanvas}
          {compact && introText ? <p className="persona-intro overview-card-intro">{introText}</p> : null}
        </div>
        {!compact && introText ? <p className="persona-intro overview-card-intro">{introText}</p> : null}
        {cardFooter}
      </>
    );

    const compactCardClass = compact
      ? ` overview-exhibition-card--compact${useSplitGrid ? ' overview-exhibition-card--split' : ''}`
      : '';

    if (disabled) {
      return (
        <div
          key={artefactName}
          className={`persona-card overview-exhibition-card persona-card--inactive${compactCardClass}`}
          aria-disabled
        >
          {inner}
        </div>
      );
    }

    return (
      <div
        key={artefactName}
        className={`persona-card overview-exhibition-card clickable${compactCardClass}`}
        role="button"
        tabIndex={0}
        onClick={() => handleArtefactActivate(artefactName)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleArtefactActivate(artefactName);
          }
        }}
      >
        {inner}
      </div>
    );
  };

  const activeName = artefactNames[activeSlide] || artefactNames[0];

  const carouselPeekPrevName =
    artefactNames.length > 0 && activeSlide > 0 ? artefactNames[activeSlide - 1] : null;
  const carouselPeekNextName =
    artefactNames.length > 0 && activeSlide < artefactNames.length - 1
      ? artefactNames[activeSlide + 1]
      : null;
  const carouselPeekNextNextName =
    artefactNames.length > 0 && activeSlide < artefactNames.length - 2
      ? artefactNames[activeSlide + 2]
      : null;
  const landingShell = (
    <div className={`App ${layoutClass} overview-app intro-landing`}>
      {overviewLandingHeader}

      <main className={`main-content ${layoutClass} overview-main`}>
        {error && <div className="overview-error overview-error--banner">{error}</div>}

        {carouselMode ? (
          <div className="overview-carousel">
            {!activeName ? (
              <div className="overview-loading overview-loading--center">{t('app.overviewNoArtefacts')}</div>
            ) : (
              // Mobile carousel: mount WebGL only on the top stack slot (was up to 4 canvases).
              <LandingCarouselSwipeCard
                swipeKey={activeName}
                peekPrevName={carouselPeekPrevName}
                peekNextName={carouselPeekNextName}
                peekNextNextName={carouselPeekNextNextName}
                canSwipeNext={activeSlide < artefactNames.length - 1}
                canSwipePrev={activeSlide > 0}
                onSwipeNext={onCarouselSwipeNext}
                onSwipePrev={onCarouselSwipePrev}
                renderStackCard={(name, { isTop } = {}) => (
                  <div className="overview-carousel-stack-card-inner">
                    {renderArtefactCard(name, { compact: false, showBlob: Boolean(isTop) })}
                  </div>
                )}
              />
            )}
            <div className="overview-carousel-dots" role="tablist" aria-label={t('app.overviewArtefacts')}>
              {artefactNames.map((name, i) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={i === activeSlide}
                  className={`overview-carousel-dot${i === activeSlide ? ' is-active' : ''}`}
                  onClick={() => {
                    if (i === activeSlide) return;
                    carouselSlideIntentRef.current = i > activeSlide ? 1 : -1;
                    setActiveSlide(i);
                  }}
                  aria-label={personaCardTitle(personas, name)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="overview-exhibition-grid overview-exhibition-grid--ipad-layout">
            <section
              className="overview-landing-hero"
              aria-labelledby="overview-landing-hero-title"
            >
              <h2 id="overview-landing-hero-title" className="overview-landing-hero__title">
                {t('introPage.landingHeroTagline')}
              </h2>
              <p className="overview-landing-hero__body">
                <span className="overview-landing-hero__body-line">{t('introPage.landingHeroBody')}</span>
              </p>
            </section>
            {artefactNames.map((name, index) =>
              renderArtefactCard(name, {
                compact: true,
                showBlob: true,
                landingGridIndex: index,
                landingGridCount: artefactNames.length,
              })
            )}
          </div>
        )}
      </main>

      <div className="overview-live-themes" aria-label={t('app.liveThemes')}>
        <span className="overview-live-themes__label">{t('app.liveThemes')}</span>
        <div className="overview-live-themes__marquee-wrap">
          {marqueeKeywordRows.length > 0 ? (
            <LandingKeywordMarquee keywords={marqueeKeywordRows} />
          ) : (
            <span className="overview-live-themes__empty">{t('app.liveThemesEmpty')}</span>
          )}
        </div>
        <OverviewFooterLinks />
      </div>
    </div>
  );

  return landingShell;
}

export default IntroPage;
